import {
  Dialog,
  DialogType,
  IMessageBar,
  Modal,
  PrimaryButton,
  Stack
} from "@fluentui/react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Contract } from "../contract.interface";
import { SalesTeamSelect } from "../ContractSalesTeamInfo";
import { ContractRoaming } from "../DisplayContractInfo/ContractRoaming";

import { PersonalizeTotalFee } from "./Confirmation/PersonalizeTotalFee";
import { MembershipValidationModal } from "./MembershipValidationModal";
import { MembershipValidationResponse } from "./MembershipValidationModal/interface";
import { SelectSignature } from "./Signature";

import { PermissionsAction } from "@/common/types/permissions.types";
import { DisplayContractInfo } from "@/components/Contracts/DisplayContractInfo";
import { Loader } from "@/components/Shared/Loader";
import { ContractsService } from "@/core/libs/api";
import {
  useGetPaymentConditions,
  useGetProfile
} from "@/core/libs/api/react-query";
import { useApi } from "@/core/libs/api/react-query/useApi";
import { MessageBar, messageBarTypes } from "@/core/libs/message-bar";
import { useConfirmModal } from "@/hooks/useConfirmModal";
import { StyledStep } from "@/modules/Contracts/styles";
import { ServicesCategoriesTypesEnum } from "@/modules/Settings/types/ServicesCategories.types";

interface ConfirmationStepProps {
  contract: Contract;
  refetchContractData?: () => Promise<void>;
  monthlyPaymentMethod: string;
  generateContractTerms: boolean;
}

function editContractRequest({ contractId, values }) {
  const abortController = new AbortController();
  const { signal } = abortController;

  const contractsService = ContractsService();
  const params = {
    id: contractId,
    body: values
  };

  return contractsService.edit({ params, signal });
}

export function ConfirmationStep(props: ConfirmationStepProps): JSX.Element {
  const { contract, refetchContractData, generateContractTerms } = props;
  const [message, setMessage] = useState<IMessageBar>(undefined);
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [membershipValidationData, setMembershipValidationData] =
    useState(null);

  const [selectedSalesTeamId, setSelectedSalesTeam] = useState<number>(
    contract?.salesTeamId
  );
  const [travelDate, setTravelDate] = useState<Date>();
  const { execute } = useApi();

  const [showSignaturesDialog, setShowSignaturesDialog] =
    useState<boolean>(false);
  const [showTermModal, setShowTermModal] = useState(false);

  const navigate = useNavigate();
  const { openModalWithSettings, setSubmittingConfirmModal } =
    useConfirmModal();

  const userProfile = useGetProfile();
  const userPermissions =
    userProfile?.data?.data?.role?.permissions?.pos?.contracts;

  const hasUpdateSalesTeamPermission: string = userPermissions.find(
    permissions => permissions === PermissionsAction.OVERWRITE_SALES_TEAM
  );

  const isRoamingContract =
    contract.service.id === 4 &&
    contract.serviceCategory.type ===
      ServicesCategoriesTypesEnum.MOBILE_LINE_PRE;

  const refetchContract = () => {
    setIsLoading(true);
    refetchContractData().finally(() => setIsLoading(false));
  };

  const openTotalFeeOverride = () => {
    setShowDialog(true);
  };

  const cleanMessage = () => {
    setMessage(undefined);
  };

  const confirmContract = async () => {
    setMessage(undefined);
    setIsSubmitting(true);

    const abortController = new AbortController();
    const { signal } = abortController;
    const contractsService = ContractsService();
    const params: any = {
      id: contract.id
    };

    const response = await contractsService.process({
      params,
      signal,
      body: { generateContractTerms }
    });

    if (response.error) {
      setMessage({
        message:
          response.error.message ??
          "Não foi possível processar o contrato, por favor tente novamente",
        type: messageBarTypes.ERROR
      });
      setIsSubmitting(false);
    } else {
      navigate(`/contracts/${contract.id}`);
    }
  };

  useEffect(() => {
    setSubmittingConfirmModal(isSubmitting);
  }, [isSubmitting, setSubmittingConfirmModal]);

  async function openModal(signatureId: string) {
    const response = await execute({
      url: `terms/validate-account/${contract.account.id}`,
      method: "GET",
      notificationMessage: "Validação de cooperado",
      params: { signatureId },
      showAlert: true,
      setIsFetching: setIsSubmitting
    });

    if (!response?.response?.ok) return;

    const data = response.data as MembershipValidationResponse;

    setMembershipValidationData(data);

    const associationRequired =
      contract.serviceModelOptions.associationRequired;

    if (associationRequired && !data.isMember) {
      setShowTermModal(true);
    } else {
      openModalConfirmContract();
    }
  }

  function openModalConfirmContract() {
    return openModalWithSettings(
      "Finalizando contrato",
      "Deseja confirmar este contrato?",
      async () => {
        await confirmContract();
      }
    );
  }

  const { data: invoiceInfos } = useGetPaymentConditions({
    contractId: props.contract.id
  });

  return (
    <>
      {isLoading || isSubmitting ? (
        <Loader />
      ) : (
        <Stack tokens={{ childrenGap: 20 }} style={{ position: "relative" }}>
          <StyledStep>
            <Stack
              tokens={{ childrenGap: 15 }}
              styles={{ root: { marginTop: 15 } }}
            >
              {message && (
                <MessageBar message={message} dismissMessage={cleanMessage} />
              )}
              <DisplayContractInfo
                clientInfoTitle={"Dados do cooperado"}
                contractInfoTitle={"Dados base"}
                contract={contract}
                refetchContractData={refetchContractData}
                className="ms-sm12"
                isInsideStep={true}
                openTotalFeeOverride={openTotalFeeOverride}
                hideContractValues={true}
                hidePaymentArea={true}
                invoiceInfos={invoiceInfos}
              />
              {hasUpdateSalesTeamPermission && (
                <SalesTeamSelect
                  salesTeamId={selectedSalesTeamId}
                  handleChangeSalesTeam={option => {
                    setSelectedSalesTeam(Number(option.key));
                  }}
                />
              )}
              {isRoamingContract && (
                <ContractRoaming
                  travelDate={travelDate}
                  setTravelDate={setTravelDate}
                />
              )}

              <Stack.Item align="center">
                <PrimaryButton
                  id="button-confirm-contract"
                  text="Confirmar Contrato"
                  onClick={() => setShowSignaturesDialog(true)}
                  disabled={isSubmitting}
                />
              </Stack.Item>
            </Stack>
          </StyledStep>

          <Modal
            isOpen={showTermModal}
            onDismiss={() => setShowTermModal(false)}
            isBlocking={false}
          >
            <MembershipValidationModal
              membershipValidationData={membershipValidationData}
              showTermModal={setShowTermModal}
            />
          </Modal>

          <SelectSignature
            showSignatures={showSignaturesDialog}
            setShowSignaturesDialog={setShowSignaturesDialog}
            contract={contract}
            refetchContract={refetchContract}
            openModalConfirmContract={openModal}
            selectedSalesTeamId={selectedSalesTeamId}
            travelDate={travelDate}
          />

          <Dialog
            hidden={!showDialog}
            dialogContentProps={{
              type: DialogType.normal,
              title: "Edição de valores",
              subText:
                "Contratos com valores personalizadas estão sujeitos à aprovação.",
              styles: { innerContent: { position: "static" } }
            }}
            onDismiss={() => setShowDialog(false)}
          >
            <PersonalizeTotalFee
              editContractRequest={editContractRequest}
              closePersonalization={() => setShowDialog(false)}
              fields={contract?.totalFeeOverrideFields}
              contractId={contract.id}
              refetchContract={refetchContract}
            />
          </Dialog>
        </Stack>
      )}
    </>
  );
}
